import { RouterStore } from '@estee/elc-router';
import { BrowserStorage } from '@estee/elc-universal-utils';
import * as Sentry from '@sentry/react';

import { IPostAuthCodeResponse, SocialSignUpType } from '~api/RampPgApiSdk';
import {
    genericSocialErrorMessage,
    IamType,
    inStoreSignUpConsentConfigName,
    socialSignUpConsentConfigName,
    StorageKey
} from '~constants/Constants';
import { isExpectedSocialLoginError, SocialSignInErrorCode } from '~constants/SignInErrorCodes';
import { SignInType } from '~constants/SignInTypeEnum';
import { SocialCallbackQueryParams } from '~constants/SocialConstants';
import { IUserFormValues } from '~domain/entities/UserFormStore';
import { ConfigRepository } from '~repositories/ConfigRepository';
import { UserRepository } from '~repositories/UserRepository';
import { CART_URL, CONSENT_ROUTE, SOCIAL_SIGN_UP_SUCCESS_ROUTE } from '~routes/routes';
import { diContainer } from '~setup/diContainer';
import serviceNames from '~setup/RampService';
import { emitAnalyticsEvent, eventTag, TaggingEventSourceType } from '~utils/Analytics';
import { getLocalStorageItem, removeLocalStorageItems } from '~utils/LocalStorageUtils';
import {
    getSocialProviderFromState,
    getSocialSignUpProviderSource,
    getSocialSignUpProviderSourceFromState,
    setSocialSignUpProviderSourceToSessionStorage
} from '~utils/SocialUtils';

import { ConfigurationLoadedUseCase } from '../ConfigurationLoadedUseCase';
import { GoToLandingViewUseCase } from './GoToLandingViewUseCase';
import { SocialSignInClickedUseCase } from './SocialSignInClickedUseCase';

enum SignInErrorSource {
    SOCIAL_PROVIDER,
    STATE_MISMATCH,
    API
}

const SOCIAL_SIGN_IN_ERROR = 'Social sign in error';

/**
 * After getting auth code from social provider
 */
export class SocialCallbackLoadedUseCase {
    private userRepository: UserRepository;
    private routerStore: RouterStore;
    private configRepository: ConfigRepository;

    private queryParams = new URLSearchParams(window.location.search);
    private callbackState: string;
    private callbackCode: string;
    private callbackError: string | null;
    private callbackErrorDescription: string | null;
    private socialProvider: SignInType | '';
    private eventSource: TaggingEventSourceType;

    constructor() {
        this.userRepository = diContainer.get(serviceNames.userRepository);
        this.routerStore = diContainer.get(serviceNames.routerStore);
        this.configRepository = diContainer.get(serviceNames.configRepository);

        this.callbackState = decodeURI(this.queryParams.get(SocialCallbackQueryParams.STATE) ?? '');
        this.callbackCode = this.queryParams.get(SocialCallbackQueryParams.CODE) ?? '';
        this.callbackError = this.queryParams.get(SocialCallbackQueryParams.ERROR);
        this.callbackErrorDescription = this.queryParams.get(
            SocialCallbackQueryParams.ERROR_DESCRIPTION
        );
        this.socialProvider = getSocialProviderFromState(this.callbackState) as SignInType | '';
        this.eventSource = BrowserStorage.getItem(
            StorageKey.SOCIAL_EVENT_SOURCE,
            'localStorage'
        ) as TaggingEventSourceType;
    }

    handleSignInError = ({
        source,
        errorCode,
        errorMessageKey = genericSocialErrorMessage
    }: {
        source: SignInErrorSource;
        errorCode: string;
        errorMessageKey?: string;
    }) => {
        emitAnalyticsEvent(eventTag.SIGN_IN_FAILED, {
            source: this.eventSource,
            type: errorCode,
            provider: this.socialProvider
        });
        if (source === SignInErrorSource.SOCIAL_PROVIDER) {
            if (!isExpectedSocialLoginError(this.callbackError || '')) {
                Sentry.captureException(new Error(SOCIAL_SIGN_IN_ERROR), {
                    extra: {
                        provider: this.socialProvider,
                        error: this.callbackError,
                        description: this.callbackErrorDescription,
                        state: this.callbackState
                    }
                });
            }
        } else if (source === SignInErrorSource.STATE_MISMATCH) {
            //line: redirect to login with disable_auto_login is true for auto login failure (state not match)
            if (this.socialProvider === SignInType.LINE) {
                new SocialSignInClickedUseCase(this.socialProvider, {
                    disable_auto_login: true
                }).execute();
            }
        } else if (
            errorCode === SocialSignInErrorCode.PROVIDER_EMAIL_MISSING &&
            this.socialProvider === SignInType.FACEBOOK
        ) {
            // https://developers.facebook.com/docs/facebook-login/guides/advanced/manual-flow#reaskperms
            this.userRepository.socialSignInState.setExtraParams(SignInType.FACEBOOK, {
                auth_type: 'rerequest'
            });
        }
        new GoToLandingViewUseCase(errorMessageKey).execute();
    };

    handleSignedIn = async (forwardToCart: boolean) => {
        const { changeLocation, changeCurrentRoute } = this.routerStore;
        const { updateUser, setIsLoading } = this.userRepository;
        const { onSuccessRedirectUrl } = this.configRepository;

        // read backup form values and userID to update user info before redirect to the 3PP site.
        const userId = getLocalStorageItem(StorageKey.CONNECT_USER_ID);
        const formValues = getLocalStorageItem(StorageKey.FORM_VALUES);
        if (userId && formValues) {
            await updateUser(Number(userId), JSON.parse(formValues) as IUserFormValues);
        }
        removeLocalStorageItems([
            StorageKey.ACCOUNT_REDIRECT_URL,
            StorageKey.SOCIAL_STATE,
            StorageKey.SOCIAL_EVENT_SOURCE,
            StorageKey.CONNECT_USER_ID,
            StorageKey.FORM_VALUES
        ]);
        emitAnalyticsEvent(eventTag.SIGN_IN_SUCCESSFUL, {
            source: this.eventSource,
            provider: this.socialProvider
        });

        const isCheckoutFlow = onSuccessRedirectUrl.includes('/checkout');
        const hasProductsInCart = forwardToCart && isCheckoutFlow;

        // If there is provider source from social callback state,
        // It is social sign up flow, need go to register success page
        if (getSocialSignUpProviderSourceFromState(this.callbackState)) {
            changeCurrentRoute(SOCIAL_SIGN_UP_SUCCESS_ROUTE);
            setIsLoading(false);
        } else {
            changeLocation(hasProductsInCart ? CART_URL : onSuccessRedirectUrl);
        }
    };

    execute = async () => {
        // TODO: APACRIGEL-2052 if no social provider, go to sign-in? covers empty callbackState case
        if (this.socialProvider) {
            const { changeCurrentRoute } = this.routerStore;
            const { setIsSocialProfile, iamType, setSocialSignUpType } = this.configRepository;
            const {
                postAuthCode,
                setIsLoading,
                consentForm: { setUsers, setIsConsentFlow, setCallbackCode, setCallbackState }
            } = this.userRepository;
            const sessionState = BrowserStorage.getItem(StorageKey.SOCIAL_STATE, 'localStorage');

            if (iamType === IamType.SOCIAL_SIGN_UP) {
                setSocialSignUpType(getSocialSignUpProviderSourceFromState(this.callbackState));
            }

            if (this.callbackState !== sessionState) {
                this.handleSignInError({
                    source: SignInErrorSource.STATE_MISMATCH,
                    errorCode: 'state mismatch'
                });
            } else {
                if (this.callbackError) {
                    this.handleSignInError({
                        source: SignInErrorSource.SOCIAL_PROVIDER,
                        errorCode: this.callbackError,
                        errorMessageKey: genericSocialErrorMessage
                    });
                } else {
                    setCallbackCode(this.callbackCode);
                    setCallbackState(this.callbackState);
                    setSocialSignUpProviderSourceToSessionStorage(this.callbackState);

                    const {
                        showConsent,
                        users = [],
                        // TODO: signed-in state
                        success,
                        error,
                        errorCode,
                        forwardToCart
                    }: IPostAuthCodeResponse = await postAuthCode({
                        authCode: this.callbackCode,
                        socialProvider: this.socialProvider,
                        state: this.callbackState,
                        providerSource: getSocialSignUpProviderSource()
                    });

                    if (error || errorCode) {
                        this.handleSignInError({
                            source: SignInErrorSource.API,
                            errorCode: errorCode as string,
                            errorMessageKey: error
                        });
                        setIsLoading(false);

                        return;
                    }

                    const consentNodeName = this.getConsentConfigurationNodeName(iamType);
                    new ConfigurationLoadedUseCase(consentNodeName).execute();

                    if (!!success) {
                        await this.handleSignedIn(!!forwardToCart);

                        return;
                    }

                    if (!!showConsent) {
                        setUsers(users);
                        setIsConsentFlow(true);
                        setIsSocialProfile(true);
                        // User is taken to consent form in these 2 conditions during social connect while consent is required
                        //  1. user is not found
                        //  2. user is found, but is not connected
                        changeCurrentRoute(CONSENT_ROUTE);
                    }

                    setIsLoading(false);
                }
            }
        }
    };

    private getConsentConfigurationNodeName(iamType: IamType) {
        if (iamType === IamType.IN_STORE) {
            return inStoreSignUpConsentConfigName;
        }

        const providerSource = getSocialSignUpProviderSource();
        if (providerSource === SocialSignUpType.KAKAO_GIFT) {
            return socialSignUpConsentConfigName;
        }

        return `ramp.${this.socialProvider}SocialSignUp`;
    }
}
